import { Box, Typography, IconButton, Divider, ListItemButton, useTheme, Card, CardContent, CardActions, Switch, Grid } from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { UserNotificationTrigger } from '../../types';

interface TriggerListItemProps {
  trigger: UserNotificationTrigger;
  onEdit: (trigger: UserNotificationTrigger) => void;
  onDelete: (id: string) => void;
  onToggleActive: (id: string, isActive: boolean) => void;
  index?: number;
  totalItems?: number;
  variant?: 'mobile' | 'desktop';
  isToggleLoading?: boolean;
}

export const TriggerListItem = ({ trigger, onEdit, onDelete, onToggleActive, index = 0, totalItems = 1, variant = 'mobile', isToggleLoading = false }: TriggerListItemProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const workflowStepPairs = trigger.workflowStepPairs || [];
  const workflowSteps = trigger.workflowSteps || [];

  const workflowSummaryText = (() => {
    if (workflowStepPairs.length > 0) {
      const counts = new Map<string, { name: string; count: number }>();
      workflowStepPairs.forEach((pair) => {
        const key = pair.workflowId;
        const name = pair.workflowName || pair.workflowId;
        const existing = counts.get(key);
        counts.set(key, {
          name,
          count: existing ? existing.count + 1 : 1,
        });
      });
      return Array.from(counts.values())
        .map((entry) => `${entry.name}: ${entry.count} ${t('userNotificationTriggers.steps')}`)
        .join(', ');
    }

    if (workflowSteps.length > 0) {
      return workflowSteps
        .map((workflow) =>
          `${workflow.workflowId}: ${workflow.stepIds.length} ${t('userNotificationTriggers.steps')}`,
        )
        .join(', ');
    }

    return trigger.workflowName || '';
  })();

  const stepSummaryText = (() => {
    if (workflowStepPairs.length === 1 && workflowStepPairs[0].stepName) {
      return workflowStepPairs[0].stepName;
    }

    const totalSteps = workflowStepPairs.length > 0
      ? workflowStepPairs.length
      : workflowSteps.reduce((total, workflow) => total + workflow.stepIds.length, 0);

    if (totalSteps > 0) {
      return `${totalSteps} ${t('userNotificationTriggers.steps')}`;
    }

    return trigger.stepName || '';
  })();

  const handleClick = () => {
    onEdit(trigger);
  };

  const handleToggleActive = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    onToggleActive(trigger.id, event.target.checked);
  };

  const renderField = (key: string, label: string, value: string | null, isValueField = false, fallback?: string) => (
    <Box key={key} display="flex" flexDirection="column" gap={0.5}>
      <Typography variant="caption" color="text.secondary" fontWeight={500}>
        {label}
      </Typography>
      <Typography 
        variant="body2" 
        color={value ? "text.primary" : "text.disabled"}
        sx={{ 
          fontWeight: isValueField ? 500 : 400,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
      >
        {value || fallback || t('common.unassigned')}
      </Typography>
    </Box>
  );

  const renderConditions = () => {
    return [
      // Client
      <Box key="client" display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="caption" color="text.secondary">
          {t('userNotificationTriggers.clientOptional')}
        </Typography>
        <Typography variant="caption" color={trigger.clientName ? "text.primary" : "text.disabled"}>
          {trigger.clientName || t('userNotificationTriggers.allClients')}
        </Typography>
      </Box>,

      // Agent
      <Box key="agent" display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="caption" color="text.secondary">
          {t('userNotificationTriggers.agentOptional')}
        </Typography>
        <Typography variant="caption" color={trigger.agentName ? "text.primary" : "text.disabled"}>
          {trigger.agentName || t('userNotificationTriggers.allAgents')}
        </Typography>
      </Box>,

      // Workflow
      <Box key="workflow" display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="caption" color="text.secondary">
          {t('userNotificationTriggers.workflow')}
        </Typography>
        <Typography variant="caption" color={workflowSummaryText ? "text.primary" : "text.disabled"}>
          {workflowSummaryText || t('userNotificationTriggers.allWorkflows')}
        </Typography>
      </Box>,

      // Step
      <Box key="step" display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="caption" color="text.secondary">
          {t('userNotificationTriggers.step')}
        </Typography>
        <Typography variant="caption" color={stepSummaryText ? "text.primary" : "text.disabled"}>
          {stepSummaryText || t('userNotificationTriggers.anyStep')}
        </Typography>
      </Box>,

      // Status
      <Box key="status" display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="caption" color="text.secondary">
          {t('userNotificationTriggers.status')}
        </Typography>
        <Typography variant="caption" color={trigger.status ? "text.primary" : "text.disabled"}>
          {trigger.status ? t(`cases.status.${trigger.status}`) : t('userNotificationTriggers.anyStatus')}
        </Typography>
      </Box>,

      // Case ID
      <Box key="case" display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="caption" color="text.secondary">
          {t('userNotificationTriggers.specificCase')}
        </Typography>
        <Typography variant="caption" color={trigger.caseId ? "text.primary" : "text.disabled"}>
          {trigger.caseId ? `#${trigger.caseId}` : t('userNotificationTriggers.anyCase')}
        </Typography>
      </Box>,
    ];
  };

  const renderDesktopFields = () => (
    <Grid container spacing={1.5}>
      <Grid item xs={12} sm={6} md={4}>
        {renderField(
          'client',
          t('userNotificationTriggers.clientOptional'),
          trigger.clientName,
          false,
          t('userNotificationTriggers.allClients')
        )}
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        {renderField(
          'agent',
          t('userNotificationTriggers.agentOptional'),
          trigger.agentName,
          false,
          t('userNotificationTriggers.allAgents')
        )}
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        {renderField(
          'workflow',
          t('userNotificationTriggers.workflow'),
          workflowSummaryText || null,
          false,
          t('userNotificationTriggers.allWorkflows')
        )}
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        {renderField(
          'step',
          t('userNotificationTriggers.step'),
          stepSummaryText || null,
          false,
          t('userNotificationTriggers.anyStep')
        )}
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        {renderField(
          'status',
          t('userNotificationTriggers.status'),
          trigger.status ? t(`cases.status.${trigger.status}`) : null,
          false,
          t('userNotificationTriggers.anyStatus')
        )}
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        {renderField(
          'case',
          t('userNotificationTriggers.specificCase'),
          trigger.caseId ? `#${trigger.caseId}` : null,
          true,
          t('userNotificationTriggers.anyCase')
        )}
      </Grid>
    </Grid>
  );

  if (variant === 'desktop') {
    return (
      <Card 
        sx={{ 
          mb: 2,
          '&:hover': {
            boxShadow: theme.shadows[4],
          },
          cursor: 'pointer',
        }}
        onClick={handleClick}
      >
        <CardContent sx={{ pb: 1 }}>
          <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" color="text.secondary">
                {t('userNotificationTriggers.active')}:
              </Typography>
              <Switch
                checked={!!trigger.enabled}
                onChange={handleToggleActive}
                size="small"
                disabled={isToggleLoading}
                onClick={(e) => e.stopPropagation()}
              />
            </Box>
          </Box>
          
          {renderDesktopFields()}
        </CardContent>
        
        <CardActions sx={{ justifyContent: 'flex-end' }}>
          <IconButton 
            size="small" 
            onClick={(e) => {
              e.stopPropagation();
              onEdit(trigger);
            }}
            sx={{ color: 'primary.main' }}
          >
            <Edit fontSize="small" />
          </IconButton>
          <IconButton 
            size="small" 
            onClick={(e) => {
              e.stopPropagation();
              onDelete(trigger.id);
            }}
            sx={{ color: 'error.main' }}
          >
            <Delete fontSize="small" />
          </IconButton>
        </CardActions>
      </Card>
    );
  }

  // Mobile variant (ListItemButton)
  return (
    <Box>
      <ListItemButton
        onClick={handleClick}
        sx={{
          py: 2,
          px: 2,
          flexDirection: 'column',
          gap: 2,
          alignItems: 'stretch',
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
          '&:active': {
            backgroundColor: theme.palette.action.selected,
          }
        }}
      >
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="caption" color="text.secondary">
              {t('userNotificationTriggers.active')}
            </Typography>
            <Switch
              checked={!!trigger.enabled}
              onChange={handleToggleActive}
              size="small"
              disabled={isToggleLoading}
              onClick={(e) => e.stopPropagation()}
            />
          </Box>
        </Box>

        <Box display="flex" flexDirection="column" gap={1}>
          {renderConditions()}
        </Box>

        <Box display="flex" justifyContent="flex-end" mt={1}>
          <IconButton 
            size="small" 
            onClick={(e) => {
              e.stopPropagation();
              onDelete(trigger.id);
            }}
            sx={{ color: 'error.main' }}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      </ListItemButton>

      {index < totalItems - 1 && <Divider />}
    </Box>
  );
};
